import { useMemo, useState } from "react";
import { Save, ZoomIn } from "lucide-react";
import {
  ALL_COUNTRIES,
  DEFAULT_ACCOUNTING_PERIODS,
} from "../AddCompany/addFacilityConstants";
import { parseAccountingPeriodToApiDates } from "../AddCompany/addFacilityMappers";
import componentIcon from "../../../assets/design-revamp-company/company-component/company-component-icon-facility.svg";
import ghgOverview from "../../../assets/company-ghg-yfs/ghg-overview.png";
import "./company-facility-ghg-yfs.css";

const pad = (n) => String(n).padStart(2, "0");

/** API / DateRangeInput: DD-MM-YYYY */
function isoToFacilityDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return "";
  return `${pad(Number(d))}-${pad(Number(m))}-${y}`;
}

function facilityDateToIso(ddmmyyyy) {
  if (!ddmmyyyy) return "";
  const [d, m, y] = ddmmyyyy.split("-");
  if (!d || !m || !y) return "";
  return `${y}-${pad(Number(m))}-${pad(Number(d))}`;
}

function formatPeriodLabel(yearStart, yearEnd) {
  const isoStart = facilityDateToIso(yearStart);
  const isoEnd = facilityDateToIso(yearEnd);
  if (!isoStart || !isoEnd) return "";
  const fmt = (iso) => {
    const [y, m, d] = iso.split("-");
    return `${pad(Number(d))}.${pad(Number(m))}.${String(y).slice(-2)}`;
  };
  const endYear = isoEnd.split("-")[0];
  const label = `${fmt(isoStart)} - ${fmt(isoEnd)} (${endYear})`;
  return DEFAULT_ACCOUNTING_PERIODS.includes(label)
    ? label
    : label;
}

function parseAddressParts(addressLine1, addressLine2, postCode, country) {
  if (!addressLine1) {
    return { street: "", postal: postCode || "", city: addressLine2 || "", country: country || "" };
  }
  const parts = addressLine1.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 4) {
    return {
      street: parts[0],
      postal: parts[1] || postCode || "",
      city: parts[2] || addressLine2 || "",
      country: parts[3] || country || "",
    };
  }
  return {
    street: parts[0] || addressLine1,
    postal: postCode || parts[1] || "",
    city: addressLine2 || parts[2] || "",
    country: country || parts[parts.length - 1] || "",
  };
}

function buildAddressLine1({ street, postal, city, country }) {
  return [street, postal, city, country]
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join(", ");
}

const FacilityGhgYfsView = ({
  t,
  name,
  addressLine1,
  addressLine2,
  postCode,
  country,
  locationNumber,
  businessSize,
  ownership,
  ownershipPerc,
  totalSpace,
  fullTimeEmployees,
  baseline,
  revenue,
  yearStart,
  yearEnd,
  types,
  setTypes,
  dropdownOwnership,
  handleInputChange,
  setOwnershipPerc,
  handleSave,
  handlePercentageChange,
  displayTextOwnership,
  errorMessage,
  isDisabled,
  userAccess,
}) => {
  const [ghgZoom, setGhgZoom] = useState(false);

  const addressParts = useMemo(
    () => parseAddressParts(addressLine1, addressLine2, postCode, country),
    [addressLine1, addressLine2, postCode, country]
  );

  const accountingPeriod = useMemo(
    () => formatPeriodLabel(yearStart, yearEnd),
    [yearStart, yearEnd]
  );

  const accountingOptions = useMemo(() => {
    const opts = [...DEFAULT_ACCOUNTING_PERIODS];
    if (accountingPeriod && !opts.includes(accountingPeriod)) {
      opts.unshift(accountingPeriod);
    }
    return opts;
  }, [accountingPeriod]);

  const selectedBuilding = useMemo(() => {
    const checked = types?.find((x) => x.checked);
    return checked?.name_en || "";
  }, [types]);

  const buildingOptions = useMemo(() => {
    return (types || []).map((x) => x.name_en).filter(Boolean);
  }, [types]);

  const updateAddressPart = (key, value) => {
    const next = { ...addressParts, [key]: value };
    const composed = buildAddressLine1(next);
    handleInputChange("addressLine1", composed);
  };

  const handleBuildingType = (nameEn) => {
    const newTypes = (types || []).map((type) => ({
      ...type,
      checked: type.name_en === nameEn,
      space_percentage: type.name_en === nameEn ? type.space_percentage ?? 100 : null,
    }));
    setTypes(newTypes);
    handleInputChange("type", newTypes.filter((x) => x.checked));
  };

  const handleAccountingPeriod = (period) => {
    const { yearStart: isoStart, yearEnd: isoEnd } =
      parseAccountingPeriodToApiDates(period);
    const fs = isoToFacilityDate(isoStart);
    const fe = isoToFacilityDate(isoEnd);
    if (fs) handleInputChange("yearStart", fs);
    if (fe) handleInputChange("yearEnd", fe);
  };

  const handleBaselineToggle = (checked) => {
    handleInputChange("baseline", checked ? t("Yes") : t("No"));
  };

  const isBaseline = baseline === t("Yes") || baseline === "Yes" || baseline === true;

  return (
    <div className="company-facility-ghg-yfs">
      <div className="company-facility-ghg-yfs__grid">
        {/* Left — identity */}
        <div className="company-facility-ghg-yfs__identity">
          <div className="company-facility-ghg-yfs__identity-inner">
            <div className="company-facility-ghg-yfs__logo-col">
              <div className="company-facility-ghg-yfs__logo-box">
                <img src={componentIcon} alt="" />
              </div>
              <textarea
                className="company-facility-ghg-yfs__facility-name"
                value={name}
                rows={1}
                maxLength={120}
                onChange={(e) => handleInputChange("name", e.target.value)}
                onInput={(e) => {
                  const el = e.currentTarget;
                  el.style.height = "auto";
                  el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
                }}
                aria-label={t("COMPANY_NAME")}
              />
            </div>

            <div className="company-facility-ghg-yfs__identity-fields">
              <div className="company-facility-ghg-yfs__field-row">
                <label>
                  <span className="company-facility-ghg-yfs__field-label">VAT no.</span>
                  <input
                    className="company-facility-ghg-yfs__field-input"
                    value=""
                    readOnly
                    placeholder="—"
                    aria-readonly
                  />
                </label>
                <label>
                  <span className="company-facility-ghg-yfs__field-label">P-nummer</span>
                  <input
                    className="company-facility-ghg-yfs__field-input"
                    value={locationNumber || ""}
                    readOnly
                    placeholder="—"
                    aria-readonly
                  />
                </label>
              </div>

              <label>
                <span className="company-facility-ghg-yfs__field-label">Street and number</span>
                <textarea
                  className="company-facility-ghg-yfs__field-input company-facility-ghg-yfs__field-input--textarea"
                  value={addressParts.street}
                  rows={1}
                  onChange={(e) => updateAddressPart("street", e.target.value)}
                  onInput={(e) => {
                    const el = e.currentTarget;
                    el.style.height = "auto";
                    el.style.height = `${el.scrollHeight}px`;
                  }}
                />
              </label>

              <div className="company-facility-ghg-yfs__field-row">
                <label>
                  <span className="company-facility-ghg-yfs__field-label">Postal</span>
                  <input
                    className="company-facility-ghg-yfs__field-input"
                    value={addressParts.postal}
                    onChange={(e) => updateAddressPart("postal", e.target.value)}
                  />
                </label>
                <label>
                  <span className="company-facility-ghg-yfs__field-label">City</span>
                  <input
                    className="company-facility-ghg-yfs__field-input"
                    value={addressParts.city}
                    onChange={(e) => updateAddressPart("city", e.target.value)}
                  />
                </label>
              </div>

              <label>
                <span className="company-facility-ghg-yfs__field-label">Country</span>
                <select
                  className="company-facility-ghg-yfs__field-input company-facility-ghg-yfs__field-select"
                  value={addressParts.country}
                  onChange={(e) => updateAddressPart("country", e.target.value)}
                >
                  <option value="">—</option>
                  {ALL_COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>

        {/* Center — introduction */}
        <div className="company-facility-ghg-yfs__intro">
          <p className="company-facility-ghg-yfs__intro-kicker">Introduction</p>
          <h1 className="company-facility-ghg-yfs__intro-title">Set up your YFS baseline</h1>
          <p>
            A climate account makes your company&apos;s emissions visible and measurable —
            the foundation for reducing your footprint, meeting reporting requirements and
            demonstrating credible climate action to customers, partners and investors. YFS
            follows the <span className="cf-intro-em">GHG Protocol</span>, the international
            standard for corporate carbon accounting, covering scope 1, 2 and 3 emissions.
          </p>
          <p>
            Define your facility, accounting period and baseline. Each YFS account adheres to a
            specific VAT number and can be split into facilities by department or building. Walk
            through each emission category, enter invoices, set system boundaries and review the
            running total — your data forms the foundation for the YFS Label.
          </p>
          <p>
            You can also import data from &quot;klimakompasset&quot; or your ERP system
            &quot;E-conomic&quot;, as well as share the flow with an auditor to get a third
            party verification.
          </p>
        </div>

        {/* Right — operational data */}
        <div className="company-facility-ghg-yfs__data-card">
          <div className="company-facility-ghg-yfs__data-inner">
            <div className="company-facility-ghg-yfs__data-fields">
              <div>
                <div className="company-facility-ghg-yfs__data-label">Building type</div>
                <select
                  className="company-facility-ghg-yfs__data-select"
                  value={selectedBuilding}
                  onChange={(e) => handleBuildingType(e.target.value)}
                >
                  <option value="">—</option>
                  {buildingOptions.map((b) => (
                    <option key={b} value={b}>
                      {t(b) !== b ? t(b) : b}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="company-facility-ghg-yfs__data-label">Ownership</div>
                <select
                  className="company-facility-ghg-yfs__data-select"
                  value={ownership}
                  onChange={(e) => handlePercentageChange(e.target.value)}
                >
                  <option value="">—</option>
                  {dropdownOwnership.map((o) => (
                    <option key={o.id} value={o.value}>
                      {o.name}
                    </option>
                  ))}
                </select>
                {!isDisabled && ownership && ownershipPerc !== "" ? (
                  <input
                    className="company-facility-ghg-yfs__data-input"
                    style={{ marginTop: 8, maxWidth: 120 }}
                    type="number"
                    min={0}
                    max={100}
                    value={ownershipPerc}
                    onChange={(e) => setOwnershipPerc(e.target.value)}
                    aria-label="Ownership percent"
                  />
                ) : null}
                {displayTextOwnership ? (
                  <p className="company-facility-ghg-yfs__lease-hint">
                    {t("LEASE_OWNERSHIP_TEXT")}
                  </p>
                ) : null}
              </div>

              <div>
                <div className="company-facility-ghg-yfs__data-label">Accounting period</div>
                <select
                  className="company-facility-ghg-yfs__data-select"
                  value={accountingPeriod}
                  onChange={(e) => handleAccountingPeriod(e.target.value)}
                >
                  <option value="">—</option>
                  {accountingOptions.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                {errorMessage?.yearEnd?.[0] ? (
                  <p className="company-facility-ghg-yfs__error">{errorMessage.yearEnd[0]}</p>
                ) : null}
              </div>

              <div>
                <div className="company-facility-ghg-yfs__data-label">Revenue</div>
                <input
                  className="company-facility-ghg-yfs__data-input"
                  value={revenue ?? ""}
                  onChange={(e) => handleInputChange("revenue", e.target.value)}
                />
              </div>

              <div className="company-facility-ghg-yfs__data-row-2">
                <div>
                  <div className="company-facility-ghg-yfs__data-label">Area</div>
                  <input
                    className="company-facility-ghg-yfs__data-input"
                    value={totalSpace ?? businessSize ?? ""}
                    readOnly
                    placeholder="m²"
                    aria-readonly
                  />
                </div>
                <div>
                  <div className="company-facility-ghg-yfs__data-label">FTE</div>
                  <input
                    className="company-facility-ghg-yfs__data-input"
                    value={fullTimeEmployees ?? ""}
                    onChange={(e) =>
                      handleInputChange("fullTimeEmployees", e.target.value)
                    }
                  />
                  {errorMessage?.fullTimeEmployees?.[0] ? (
                    <p className="company-facility-ghg-yfs__error">
                      {errorMessage.fullTimeEmployees[0]}
                    </p>
                  ) : null}
                </div>
              </div>

              <div>
                <div className="company-facility-ghg-yfs__data-label">Baseline</div>
                <label className="company-facility-ghg-yfs__baseline">
                  <input
                    type="checkbox"
                    checked={isBaseline}
                    onChange={(e) => handleBaselineToggle(e.target.checked)}
                  />
                  {isBaseline ? t("Yes") : t("No")}
                </label>
              </div>
            </div>

            <div className="company-facility-ghg-yfs__ghg-thumb-wrap">
              <button
                type="button"
                className="company-facility-ghg-yfs__ghg-thumb"
                onClick={() => setGhgZoom(true)}
                aria-label="Enlarge GHG overview"
              >
                <img src={ghgOverview} alt="GHG Protocol overview" />
              </button>
              <button
                type="button"
                className="company-facility-ghg-yfs__ghg-zoom-btn"
                onClick={() => setGhgZoom(true)}
                aria-label="Zoom GHG overview"
              >
                <ZoomIn size={28} strokeWidth={1.75} />
              </button>
            </div>
          </div>

          {userAccess === "write" && (
            <div className="company-facility-ghg-yfs__footer">
              <button
                type="button"
                className="company-facility-ghg-yfs__save-btn"
                onClick={handleSave}
              >
                <Save size={16} />
                {t("SAVE")}
              </button>
            </div>
          )}
        </div>
      </div>

      {ghgZoom && (
        <div
          className="company-facility-ghg-yfs__modal-backdrop"
          role="presentation"
          onClick={() => setGhgZoom(false)}
        >
          <div
            className="company-facility-ghg-yfs__modal"
            role="dialog"
            aria-modal="true"
            aria-label="GHG Protocol overview"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="company-facility-ghg-yfs__modal-close"
              onClick={() => setGhgZoom(false)}
              aria-label="Close"
            >
              ×
            </button>
            <img src={ghgOverview} alt="GHG Protocol overview" />
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityGhgYfsView;
